/**
 * /checkin/register — mobile-first adult-only registration.
 *
 * Six required fields per the master spec. Under-18 attempts hit a
 * respectful gate at the backend that only records an anonymous count.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  CheckInShell,
  CheckInLogo,
  PrimaryButton,
  TextField,
  CHECKIN_PALETTE,
} from "./_shell";
import { checkinApi, setToken, setCachedUser } from "@/lib/checkinApi";

export default function CheckInRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_initial: "", email: "", age: "", password: "",
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    if (!consent) {
      setErr("Please acknowledge the disclosure to continue.");
      return;
    }
    const ageNum = parseInt(form.age, 10);
    if (Number.isNaN(ageNum) || ageNum < 1) {
      setErr("Please enter your age.");
      return;
    }
    setSubmitting(true);
    try {
      const out = await checkinApi.register({
        first_name: form.first_name.trim(),
        last_initial: form.last_initial.trim().slice(0, 1),
        email: form.email.trim().toLowerCase(),
        age: ageNum,
        password: form.password,
        consent_accepted: true,
      });
      setToken(out.token);
      setCachedUser(out.user);
      navigate("/checkin/onboarding", { replace: true });
    } catch (e2) {
      if (e2.code === "under_minimum_age") {
        // Route to the respectful under-18 gate. No PII persisted.
        navigate("/checkin/adults-only", { replace: true });
        return;
      }
      setErr(e2.message || "We couldn't create your account. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <CheckInShell testid="checkin-register">
      <div className="pt-2 flex items-center justify-between">
        <CheckInLogo small />
        <Link to="/checkin" className="text-xs" style={{ color: CHECKIN_PALETTE.purple }}
              data-testid="register-back">
          ← Back
        </Link>
      </div>

      <div className="mt-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.28em]"
             style={{ color: CHECKIN_PALETTE.purple }}>
          Start your check-in
        </div>
        <h1 className="mt-2 font-serif text-3xl leading-tight"
            style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Let&rsquo;s get you set up.
        </h1>
        <p className="mt-3 text-[13.5px]" style={{ color: "#5C5468" }}>
          A short profile — we only ask what we need.
        </p>
      </div>

      <form className="mt-6 space-y-4" onSubmit={onSubmit} data-testid="register-form">
        <div className="grid grid-cols-[1fr_88px] gap-3">
          <TextField
            label="First name" testid="register-first-name" required
            autoComplete="given-name" value={form.first_name}
            onChange={(e) => up("first_name", e.target.value)}
            placeholder="Maya"
          />
          <TextField
            label="Initial" testid="register-last-initial" required
            maxLength={1} value={form.last_initial}
            onChange={(e) => up("last_initial", e.target.value.replace(/[^A-Za-z]/g, ""))}
            placeholder="K"
          />
        </div>

        <TextField
          label="Email" testid="register-email" required type="email"
          autoComplete="email" inputMode="email" value={form.email}
          onChange={(e) => up("email", e.target.value)}
          placeholder="you@example.com"
        />

        <TextField
          label="Age" testid="register-age" required type="number"
          inputMode="numeric" min="1" max="120" value={form.age}
          onChange={(e) => up("age", e.target.value)}
          placeholder="34"
        />

        <TextField
          label="Create a password" testid="register-password" required
          type="password" autoComplete="new-password" minLength={8}
          value={form.password} onChange={(e) => up("password", e.target.value)}
          placeholder="At least 8 characters"
        />

        <label className="flex items-start gap-3 pt-2 select-none"
               data-testid="register-consent">
          <input
            type="checkbox" className="mt-1 w-4 h-4"
            checked={consent} onChange={(e) => setConsent(e.target.checked)}
            data-testid="register-consent-checkbox"
            style={{ accentColor: CHECKIN_PALETTE.purple }}
          />
          <span className="text-[12.5px] leading-relaxed" style={{ color: "#4A4256" }}>
            I understand that Check In with Dr. C uses technology trained on
            Dr. C&rsquo;s approved voice and frameworks; responses may not be
            written live by Dr. C; and this service is not for emergencies,
            diagnosis, or therapy. I agree to the{" "}
            <Link to="/checkin/policies" className="underline"
                  style={{ color: CHECKIN_PALETTE.purple }}>
              Terms, Privacy Notice, and Companion Disclosure
            </Link>.
          </span>
        </label>

        {err ? (
          <div className="rounded-2xl border px-4 py-3 text-sm"
               style={{ borderColor: CHECKIN_PALETTE.danger, color: CHECKIN_PALETTE.danger, background: "#FDF1F3" }}
               data-testid="register-error">
            {err}
          </div>
        ) : null}

        <div className="pt-2">
          <PrimaryButton
            type="submit"
            disabled={submitting || !consent}
            testid="register-submit"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Creating your space…
              </span>
            ) : (
              "Continue"
            )}
          </PrimaryButton>
        </div>

        <p className="text-center text-[12px] pt-2" style={{ color: "#6C6478" }}>
          Already have an account?{" "}
          <Link to="/checkin/signin" className="underline"
                style={{ color: CHECKIN_PALETTE.purple }} data-testid="register-signin-link">
            Sign in
          </Link>
        </p>
      </form>
    </CheckInShell>
  );
}
